import { Button, Card, Chip, EmptyState, Segmented } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Handshake, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CommitmentDialog } from '../../components/people/CommitmentDialog.tsx'
import { CommitmentRow } from '../../components/people/CommitmentRow.tsx'
import { commitmentsQuery } from '../../lib/m2.ts'
import { commitmentSummaryQuery } from '../../lib/people-m4.ts'

type Tab = 'i_owe' | 'owed_to_me'
export const Route = createFileRoute('/_app/zusagen')({
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    s.tab === 'owed_to_me' ? { tab: 'owed_to_me' } : {},
  component: CommitmentsPage,
})

function CommitmentsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const tab: Tab = search.tab ?? 'i_owe'
  const [showClosed, setShowClosed] = useState(false)
  const [creating, setCreating] = useState(false)
  const [due, setDue] = useState<'all' | 'overdue' | 'week'>('all')
  const summary = useQuery(commitmentSummaryQuery())
  const q = useQuery(
    commitmentsQuery({
      direction: tab,
      status: showClosed && due === 'all' ? undefined : 'open',
      ...(due === 'all' ? {} : { due }),
    }),
  )
  const s = summary.data?.[tab === 'i_owe' ? 'iOwe' : 'owedToMe']
  const items = q.data ?? []
  return (
    <div className="min-w-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-3xl flex-col gap-4 px-4 py-4 md:px-8 md:py-6">
        <header data-tauri-drag-region className="flex items-center gap-3">
          <h1 className="flex-1 text-[22px] text-text-1">{t('nav.commitments')}</h1>
          <Button size={32} variant="primary" onClick={() => setCreating(true)}>
            <Plus size={14} />
            {t('commitments.new')}
          </Button>
        </header>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            aria-label={t('nav.commitments')}
            value={tab}
            onValueChange={(v) => void navigate({ search: v === 'i_owe' ? {} : { tab: v } })}
            options={[
              { value: 'i_owe', label: t('commitments.iOwe') },
              { value: 'owed_to_me', label: t('commitments.owedToMe') },
            ]}
          />
          <Segmented
            aria-label={t('crm.commitments.filter')}
            value={due}
            onValueChange={(v) => setDue(v as typeof due)}
            options={[
              { value: 'all', label: t('crm.commitments.all') },
              { value: 'overdue', label: t('crm.commitments.overdue', { count: s?.overdue ?? 0 }) },
              { value: 'week', label: t('crm.commitments.week') },
            ]}
          />
          <span className="flex-1" />
          <Button
            size={32}
            variant={showClosed ? 'secondary' : 'ghost'}
            onClick={() => setShowClosed((v) => !v)}
          >
            {t(showClosed ? 'commitments.hideClosed' : 'commitments.showClosed')}
          </Button>
        </div>
        {s && (s.overdue > 0 || s.dueThisWeek > 0) && (
          <div className="flex flex-wrap gap-2">
            {s.overdue > 0 && (
              <Chip tone="apricot">{t('crm.commitments.overdueChip', { count: s.overdue })}</Chip>
            )}
            {s.dueThisWeek > 0 && (
              <Chip tone="sky">{t('crm.commitments.weekChip', { count: s.dueThisWeek })}</Chip>
            )}
            <Chip>{t('crm.commitments.openChip', { count: s.open })}</Chip>
          </div>
        )}
        {q.isPending ? (
          <p className="text-[13px] text-meta">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Handshake size={20} strokeWidth={1.9} />}
            title={t(tab === 'i_owe' ? 'commitments.emptyIOwe' : 'commitments.emptyOwed')}
            description={t('commitments.emptyText')}
          />
        ) : (
          <Card className="p-2">
            {items.map((c) => (
              <CommitmentRow key={c.id} c={c} />
            ))}
          </Card>
        )}
        {creating && <CommitmentDialog direction={tab} onClose={() => setCreating(false)} />}
      </div>
    </div>
  )
}
